import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import useLogout from '../../../hooks/auth/authHook.js';
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { encryptPassword } from '../../../utils/Encryption';
import {
  Container,
  Dialog,
  Typography,
  Button,
  InputAdornment,
  IconButton,
  Grid,
  Input,
  CircularProgress,
} from "@mui/material";
import { updateUserPassword } from "../../../api/userApi";
import { notification } from "../../../utils/Notifications.js";
import styles from "./usersNewPassowordDialogStyles.js";
import { TickCircle } from "iconsax-react";

UsersNewPasswordFormDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
};

export default function UsersNewPasswordFormDialog({ onClose, open, user, fetchUsers }) {
  const [logout] = useLogout();

  const [containsRightLenght, setContainsRightLenght] = useState(false);
  const [containsLetter, setContainsLetter] = useState(false);
  const [containsNumbers, setContainsNumbers] = useState(false);
  const [containsSpecialCharacters, setContainsSpecialCharacters] = useState(false);
  const [areSamePasswords, setAreSamePasswords] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmedPassword, setConfirmedPassword] = useState("");
  const [isShowingPassword, setIsShowingPassword] = useState(false);
  const [isShowingConfirmedPassword, setIsShowingConfirmedPassword] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);

  const handleMouseDownPassword = (event) => event.preventDefault();
  const handlePasswordChange = (event) => setPassword(event.target.value);
  const handleConfirmedPasswordChange = (event) => setConfirmedPassword(event.target.value);
  const handleIsShowingPassword = (event) => setIsShowingPassword(!isShowingPassword);
  const handleIsShowingConfirmedPassword = (event) => setIsShowingConfirmedPassword(!isShowingConfirmedPassword);

  const validatePassword = (password, confirmedPassword) => {
    const letterRegex = /[A-Za-z]/;
    const numberRegex = /[0-9]/;
    const specialCharRegex = /[!@#$%^&*]/;

    const containsRightLenght = password.length >= 8;
    const containsLetter = letterRegex.test(password);
    const containsNumbers = numberRegex.test(password);
    const containsSpecialCharacters = specialCharRegex.test(password);
    const areSamePasswords = password === confirmedPassword && password !== "";

    setContainsRightLenght(containsRightLenght);
    setContainsLetter(containsLetter);
    setContainsNumbers(containsNumbers);
    setContainsSpecialCharacters(containsSpecialCharacters);
    setAreSamePasswords(areSamePasswords);

    return containsLetter && containsNumbers && containsSpecialCharacters && areSamePasswords && containsRightLenght;
  };

  useEffect(() => {
    const isValid = validatePassword(password, confirmedPassword);
    setIsFormValid(isValid);
  }, [password, confirmedPassword]);

  const handleDialogClose = (event, reason) => {
    if (reason === 'backdropClick') {
      return;
    }
    setPassword('');
    setConfirmedPassword('');
    setIsShowingPassword(false);
    setIsShowingConfirmedPassword(false);
    setContainsLetter(false);
    setContainsNumbers(false);
    setContainsSpecialCharacters(false);
    setAreSamePasswords(false);
    onClose();
  };

  const handleSavePasswordClick = () => {
    updatePassword();
  };

  const updatePassword = () => {

    if(!isFormValid) { return; }
    if(isLoading) { return; }
    setIsLoading(true);

    const hashedPassword = encryptPassword(password);

    updateUserPassword(user.id, hashedPassword)
      .then((response) => {
        if (response.data.status_Code === 200) {
          notification({ text: "Contraseña cambiada", type: "success" });
          fetchUsers();
          handleDialogClose();
        } else if (response.data.status_Code === 401) {
          notification({
            text: "Sesión expirada. Por favor, inicia sesión nuevamente.",
            type: "error",
          });
          logout();
        } else {
          notification({
            text: "Error al cambiar la contraseña",
            type: "error",
          });
        }
      })
      .catch((error) => {
        notification({text: 'Error de conexión, revisa tu conexión a internet', type: 'error'});
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  return (
    <Dialog
      onClose={handleDialogClose}
      open={open}
      PaperProps={{ style: styles.dialog }}
    >
      <Container style={{ padding: 36, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography
          component="div"
          variant="h5"
          style={styles.title}
        >
          Cambiar contraseña
        </Typography>
        <Typography
          component="div"
          variant="body2"
          style={styles.subtitle}
        >
          La contraseña debe tener al menos ocho caracteres e incluir números y caracteres especiales
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={0} sm={2} md={2}></Grid>
          <Grid item xs={12} sm={8} md={8}>
            <Input
              id="new-password-field"
              type={isShowingPassword ? "text" : "password"}
              value={password}
              style={{ width: '100%' }}
              variant="standard"
              onChange={handlePasswordChange}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleIsShowingPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {isShowingPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              }
              placeholder="* Nueva contraseña"
            />
          </Grid>
          <Grid item xs={12} sm={2} md={2}></Grid>
          <Grid item xs={0} sm={2} md={2}></Grid>
          <Grid item xs={12} sm={8} md={8}>
            <Input
              id="filled-adornment-password"
              placeholder="* Confirma la contraseña"
              variant="standard"
              style={{ width: '100%' }}
              type={isShowingConfirmedPassword ? "text" : "password"}
              value={confirmedPassword}
              onChange={handleConfirmedPasswordChange}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleIsShowingConfirmedPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {isShowingConfirmedPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              }
            />
          </Grid>
          <Grid item xs={0} sm={2} md={2}></Grid>
        </Grid>

        <Container style={styles.rulesContainer}>
          <Container style={styles.ruleContainer}>
            <TickCircle size="18" style={containsRightLenght ? styles.successRuleIcon : styles.errorRuleIcon} variant="Bold"/>
            <Typography variant="body2">Contiene 8 caracteres como mínimo</Typography>
          </Container>
          <Container style={styles.ruleContainer}>
            <TickCircle size="18" style={containsLetter ? styles.successRuleIcon : styles.errorRuleIcon} variant="Bold"/>
            <Typography variant="body2">Contiene letras</Typography>
          </Container>
          <Container style={styles.ruleContainer}>
            <TickCircle size="18" style={containsNumbers ? styles.successRuleIcon : styles.errorRuleIcon} variant="Bold"/>
            <Typography variant="body2">Contiene números</Typography>
          </Container>
          <Container style={styles.ruleContainer}>
            <TickCircle size="18" style={containsSpecialCharacters ? styles.successRuleIcon : styles.errorRuleIcon} variant="Bold"/>
            <Typography variant="body2">Contiene caracteres especiales</Typography>
          </Container>
          <Container style={styles.ruleContainer}>
            <TickCircle size="18" style={areSamePasswords ? styles.successRuleIcon : styles.errorRuleIcon} variant="Bold"/>
            <Typography variant="body2">Las contraseñas son iguales</Typography>
          </Container>
        </Container>

        <Container
          style={styles.buttonsContainer}
        >
          <Button
            variant="standard"
            color="primary"
            onClick={() => {
              handleDialogClose();
            }}
            style={styles.cancelButton}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={!isFormValid}
            onClick={() => {
              handleSavePasswordClick();
            }}
            sx={isFormValid ? styles.activeButton : styles.inactiveButton}
          >
            {isLoading ? <CircularProgress size={24} sx={styles.circularProgress} /> : 'Guardar'}
          </Button>
        </Container>
      </Container>
    </Dialog>
  );
}
